import React, { useEffect, useMemo } from 'react';
import { IconButton, Page, PageContentHeader, TableList, Typography, WithInfoIcon, useGlobalContext } from 'seagull';
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { selectors } from "../../store/state-selectors";
import AnchorBillingEmptyState from "./AnchorBillingEmptyState.svg";
import styles from "./AnchorBilling.module.scss";
import { handleRequest } from "../../infra/utils";
import { utils } from "../../utils";
import { timeModel } from "../../models";
import { DownloadLiner24 } from 'seagull/icons';
const testPrefix = 'billing';
const title = 'Anchor Billing';
const downloadLoader = 'getDownloadURL';

function AnchorBillingWithData(props) {
  const {
    serviceInvoices,
    credit,
    worksAt
  } = props;
  const {
    api
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const {
    openNewTab
  } = useIoContext();
  const serviceFee = worksAt.serviceFee ? worksAt.serviceFee.payAsYouGo : 0;
  const {
    columnDefs,
    headerItems
  } = useMemo(() => {
    const tableConfig = [{
      columnDef: {
        width: 200
      },
      headerItem: {
        value: 'Invoice #'
      }
    }, {
      columnDef: {
        width: 130
      },
      headerItem: {
        value: 'Due date'
      }
    }, {
      columnDef: {
        width: 100,
        hAlign: 'center'
      },
      headerItem: {
        value: /*#__PURE__*/React.createElement(WithInfoIcon, {
          info: "Fee of ".concat(utils.formatDollarAmount(serviceFee), " is charged per payment.")
        }, "Quantity")
      }
    }, {
      columnDef: {
        width: 150
      },
      headerItem: {
        value: 'Sum'
      }
    }];
    return {
      columnDefs: tableConfig.map(c => c.columnDef),
      headerItems: tableConfig.map(c => c.headerItem)
    };
  }, [serviceFee]);

  function getRowItems(serviceInvoice, index) {
    var _serviceInvoice$invoi, _serviceInvoice$invoi2;

    return [{
      value: serviceInvoice.formattedNumber,
      ['data-testid']: "".concat(testPrefix, "-row-").concat(index, "-invoice-number")
    }, {
      value: timeModel.formatDate(new Date(serviceInvoice.dueDate)),
      ['data-testid']: "".concat(testPrefix, "-row-").concat(index, "-due-date")
    }, {
      value: serviceInvoice.quantity,
      ['data-testid']: "".concat(testPrefix, "-row-").concat(index, "-quantity")
    }, {
      value: /*#__PURE__*/React.createElement(WithInfoIcon, {
        showInfo: !!((_serviceInvoice$invoi = serviceInvoice.invoices) !== null && _serviceInvoice$invoi !== void 0 && _serviceInvoice$invoi.length),
        infoTestId: "".concat(testPrefix, "-row-").concat(index, "-related-invoices"),
        info: /*#__PURE__*/React.createElement("div", {
          "data-testid": "".concat(testPrefix, "-row-").concat(index, "-related-invoices-tooltip")
        }, /*#__PURE__*/React.createElement(Typography, {
          skin: "invertedStandard",
          weight: "bold"
        }, "Related invoices"), (_serviceInvoice$invoi2 = serviceInvoice.invoices) === null || _serviceInvoice$invoi2 === void 0 ? void 0 : _serviceInvoice$invoi2.map(invoice => /*#__PURE__*/React.createElement("div", {
          key: invoice.id
        }, "Invoice ".concat(invoice.formattedNumber))))
      }, /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        skin: "secondary"
      }, utils.formatDollarAmount(serviceInvoice.total))),
      ['data-testid']: "".concat(testPrefix, "-row-").concat(index, "-sum")
    }];
  }

  async function openPdf(pdfFile) {
    const {
      fileName
    } = pdfFile;
    globalContext.startLoader(downloadLoader);
    const [response,, hasError] = await handleRequest(api.serviceInvoices.getDownloadURL(fileName));
    globalContext.stopLoader(downloadLoader);

    if (!hasError) {
      openNewTab(response.url, '_blank');
    }
  }

  return /*#__PURE__*/React.createElement(Page, {
    className: styles.billingDashboard,
    header: title,
    actions: !!credit && /*#__PURE__*/React.createElement(Typography, {
      weight: "normal"
    }, "Remaining credit: ", utils.formatDollarAmount(credit)),
    "data-testid": "".concat(testPrefix, "-screen")
  }, serviceInvoices.length > 0 ? /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs,
    hasActionColumn: true
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems
  })), serviceInvoices.map((serviceInvoice, index) => /*#__PURE__*/React.createElement(TableList.Row, {
    key: serviceInvoice.id,
    items: getRowItems(serviceInvoice, index),
    actionColumn: /*#__PURE__*/React.createElement("div", {
      "data-testid": "".concat(testPrefix, "-row-").concat(index, "-pdf")
    }, serviceInvoice.pdfFile.status === 'done' && /*#__PURE__*/React.createElement(IconButton, {
      "data-testid": "".concat(testPrefix, "-row-").concat(index, "-download"),
      onClick: () => openPdf(serviceInvoice.pdfFile)
    }, /*#__PURE__*/React.createElement(DownloadLiner24, null)))
  }))) : /*#__PURE__*/React.createElement("div", {
    className: styles.emptyStateWrapper
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles.title
  }, "Anchor fees"), /*#__PURE__*/React.createElement("div", {
    className: styles.subTitle
  }, "Anchor fees will appear here."), /*#__PURE__*/React.createElement("div", {
    className: styles.subTitle
  }, "Fees are charged for invoices over $10."), /*#__PURE__*/React.createElement(AnchorBillingEmptyState, null))));
}

export default function AnchorBilling() {
  const worksAt = useStore(selectors.user.business);
  const {
    api
  } = useStoreActions();
  useEffect(() => {
    document.title = title;
  }, []);

  if (!worksAt) {
    throw new Error('no business');
  }

  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => Promise.all([api.serviceInvoices.getByBusinessId(worksAt.id), api.serviceInvoices.getCredit()]),
    renderChild: _ref => {
      let {
        data: [serviceInvoices, credit]
      } = _ref;
      return /*#__PURE__*/React.createElement(AnchorBillingWithData, {
        serviceInvoices: serviceInvoices,
        credit: credit,
        worksAt: worksAt
      });
    }
  });
}